import React, { useEffect, useState, useCallback } from "react";
import { LogIn, LogOut, Edit3, MessageSquare, RefreshCw, UserCog, Search } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Pagination, Select, Avatar, Spinner } from "../components/ui";
import { format } from "date-fns";

const TYPE_META = {
  login: { icon: LogIn, cls: "text-emerald-500 bg-emerald-100 dark:bg-emerald-500/15" },
  logout: { icon: LogOut, cls: "text-slate-500 bg-slate-100 dark:bg-slate-700/40" },
  client_update: { icon: Edit3, cls: "text-blue-500 bg-blue-100 dark:bg-blue-500/15" },
  comment: { icon: MessageSquare, cls: "text-indigo-500 bg-indigo-100 dark:bg-indigo-500/15" },
  reassignment: { icon: UserCog, cls: "text-amber-500 bg-amber-100 dark:bg-amber-500/15" },
  deposit: { icon: RefreshCw, cls: "text-emerald-500 bg-emerald-100 dark:bg-emerald-500/15" },
};

const FILTERS = [
  { value: "login", label: "Login" }, { value: "logout", label: "Logout" },
  { value: "client_update", label: "Client Update" }, { value: "comment", label: "Comment" },
  { value: "reassignment", label: "Reassignment" }, { value: "deposit", label: "Deposit" },
];

export default function ActivityLogs() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [agent, setAgent] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [agents, setAgents] = useState([]);
  const pageSize = 30;

  useEffect(() => { const t = setTimeout(() => setDebounced(search), 400); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [type, agent, debounced]);
  useEffect(() => { if (isAdmin) api.get("/users/simple").then((r) => setAgents(r.data)); }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/activity-logs", { params: { action_type: type || undefined, user_id: agent || undefined, search: debounced || undefined, page, page_size: pageSize } });
      setData(r.data);
    } finally { setLoading(false); }
  }, [type, agent, debounced, page]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-display tracking-tight text-slate-900 dark:text-white">Activity Logs</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{data.total.toLocaleString()} total events</p>
        </div>
        <button className="btn-secondary" onClick={load} data-testid="logs-refresh"><RefreshCw className="w-4 h-4" /> Refresh</button>
      </div>

      <div className="card p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input className="input pl-9" placeholder="Search actions..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="logs-search" />
        </div>
        <Select value={type} onChange={setType} placeholder="All Types" options={FILTERS} testid="logs-type" />
        {isAdmin ? <Select value={agent} onChange={setAgent} placeholder="All Users" options={agents.map((a) => ({ value: a.id, label: a.full_name }))} testid="logs-user" /> : <div />}
      </div>

      <div className="card divide-y divide-slate-100 dark:divide-slate-800" data-testid="logs-list">
        {loading ? <div className="py-16 text-center"><Spinner className="w-6 h-6 inline text-slate-400" /></div> :
          data.items.length === 0 ? <div className="py-16 text-center text-slate-400">No activity found</div> :
          data.items.map((l) => {
            const meta = TYPE_META[l.action_type] || TYPE_META.client_update;
            const Icon = meta.icon;
            return (
              <div key={l.id} className="flex items-center gap-4 px-5 py-3.5">
                <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${meta.cls}`}><Icon className="w-4 h-4" /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-800 dark:text-slate-100 truncate">{l.action}</div>
                  <div className="text-xs text-slate-400 flex items-center gap-2 mt-0.5">
                    <span className="font-medium text-slate-500 dark:text-slate-400">{l.user_name}</span>
                    <span>•</span>
                    <span className="capitalize">{l.action_type.replace("_", " ")}</span>
                  </div>
                </div>
                <div className="text-xs text-slate-400 whitespace-nowrap text-right">
                  {l.created_at ? format(new Date(l.created_at), "MMM d, yyyy") : ""}<br />
                  <span className="text-slate-300 dark:text-slate-500">{l.created_at ? format(new Date(l.created_at), "h:mm a") : ""}</span>
                </div>
              </div>
            );
          })}
        <div className="px-5"><Pagination page={page} pageSize={pageSize} total={data.total} onPage={setPage} /></div>
      </div>
    </div>
  );
}
